<template>
  <div class="pet-create-container">
    <!-- 头部区域 -->
    <div class="header-section">
      <h1 class="main-title">{{ $t("messages.title") }}</h1>
      <p class="subtitle">{{ $t("messages.slog") }}</p>
    </div>

    <!-- 表单卡片 -->
    <div class="form-card">
      <div class="card-header">
        <h2 class="section-title">{{ $t("messages.public_data") }}</h2>
        <p class="section-tips">{{ $t("messages.data_tips") }}</p>
      </div>

      <van-form @submit="onSubmit" class="custom-form">
        <!-- 图片上传 -->
        <div class="upload-section">
          <van-uploader 
            v-model="fileList" 
            :after-read="afterRead" 
            :max-count="1"
            preview-size="100px"
          />
        </div>

        <!-- 基本信息 -->
        <van-cell-group inset class="form-group">
          <van-field
            v-model="petname"
            name="Pet Name"
            :label="$t('messages.pet_name')"
            :placeholder="$t('messages.pet_name')"
            :rules="[{ required: true, message: $t('messages.pet_name_tips') }]"
          >
            <template #left-icon>
              <span class="material-icons" style="font-size: 18px; margin-right: 4px; color: #5072ff;">pets</span>
            </template>
          </van-field>
          <van-field
            readonly
            clickable
            is-link
            name="datetimePicker"
            v-model="birthday"
            :label="$t('messages.birthday')"
            :placeholder="$t('messages.birthday_tips')"
            @click="showPickdate = true"
          >
            <template #left-icon>
              <span class="material-icons" style="font-size: 18px; margin-right: 4px; color: #5072ff;">cake</span>
            </template>
          </van-field>
          <van-field
            v-model="weightValue"
            name="Weight"
            :label="$t('messages.weight')"
            :placeholder="$t('messages.weight')"
            type="number"
            :rules="[{ required: false, message: $t('messages.weight_tips') }]"
          >
            <template #left-icon>
              <span class="material-icons" style="font-size: 18px; margin-right: 4px; color: #5072ff;">monitor_weight</span>
            </template>
            <template #right-icon>
              <span 
                style="color: #5072ff; font-size: 14px; font-weight: 600; cursor: pointer; padding: 4px 8px;"
                @click="toggleWeightUnit"
              >
                {{ weightUnit }}
              </span>
            </template>
          </van-field>
        </van-cell-group>

        <!-- 联系信息 -->
        <van-cell-group inset class="form-group">
          <van-field
            v-model="phone"
            name="Phone"
            :label="$t('messages.phone')" 
            type="tel"
            :placeholder="$t('messages.phone')"
            :rules="[{ required: false, message: $t('messages.phone_tips') }]"
          >
            <template #left-icon>
              <span class="material-icons" style="font-size: 18px; margin-right: 4px; color: #5072ff;">phone</span>
            </template>
          </van-field>
          <van-field
            v-model="email"
            name="Email"
            :label="$t('messages.email')"
            :placeholder="$t('messages.email_tips')"
            type="email"
            :rules="[{ required: false, message: $t('messages.email_tips') }]"
          >
            <template #left-icon>
              <span class="material-icons" style="font-size: 18px; margin-right: 4px; color: #5072ff;">email</span>
            </template>
          </van-field>
        </van-cell-group>

        <!-- 备注 -->
        <van-cell-group inset class="form-group">
          <van-field
            v-model="note"
            name="Note"
            :label="$t('messages.note')"
            :placeholder="$t('messages.note_tips')"
            type="textarea"
            rows="4"
            autosize
            maxlength="200"
            show-word-limit
            :rules="[{ required: false, message: $t('messages.note_tips') }]"
          >
            <template #left-icon>
              <span class="material-icons" style="font-size: 18px; margin-right: 4px; color: #5072ff;">edit_note</span>
            </template>
          </van-field>
        </van-cell-group>

        <!-- 修改密钥 -->
        <div class="key-section">
          <h3 class="key-title">{{ $t("messages.modify_key") }}</h3>
          <p class="key-tips">{{ $t("messages.modify_key_tips") }}</p>
          <van-cell-group inset>
            <van-field
              v-model="modify_key"
              name="Modify key"
              :label="$t('messages.modify_key')"
              :placeholder="$t('messages.modify_key')"
              type="password"
              :rules="[{ required: true, message: $t('messages.modify_key_ruler') }]"
            >
              <template #left-icon>
                <span class="material-icons" style="font-size: 18px; margin-right: 4px; color: #5072ff;">lock</span>
              </template>
            </van-field>
          </van-cell-group>
        </div>

        <!-- 提交按钮 -->
        <div class="submit-section">
          <van-button 
            round 
            block 
            type="primary" 
            native-type="submit"
            size="large"
            class="submit-btn"
            :loading="loading_state"
            :disabled="loading_state"
          >
            {{ loading_state ? $t('messages.submitting') : $t("messages.submit") }}
          </van-button>
        </div>
      </van-form>
    </div>

    <!-- 日期选择器 -->
    <van-popup v-model:show="showPickdate" position="bottom" round>
      <van-date-picker
        v-model="currentDate"
        :title="$t('messages.birthday_select')"
        :max-date="maxDate"
        :min-date="minDate"
        @confirm="onConfirm"
        @cancel="showPickdate = false"
        :confirm-button-text="$t('messages.ok')"
        :cancel-button-text="$t('messages.cancel')"
      />
    </van-popup>

    <!-- 体重单位选择器 -->
    <van-popup v-model:show="showWeightUnitPicker" position="bottom" round>
      <van-picker
        :columns="weightUnitColumns"
        @confirm="onWeightUnitConfirm"
        @cancel="showWeightUnitPicker = false"
        :confirm-button-text="$t('messages.ok')"
        :cancel-button-text="$t('messages.cancel')"
      />
    </van-popup>

    <!-- 图片裁剪 -->
    <h5-cropper 
      ref="cropper" 
      :option="option" 
      hide-input="true" 
      @get-file="getFile"
      @canceltailor="canceltailor"
    ></h5-cropper>

    <!-- 加载弹窗 -->
    <van-popup v-model:show="loading_state" round :style="{ padding: '40px' }">
      <van-loading color="#5072ff" size="48px" vertical>{{ loading_state ? $t('messages.submitting') : $t('messages.loading') }}</van-loading>
    </van-popup>
  </div>
</template>

<script>
import { Form, Field, CellGroup,Button,Uploader,Popup,DatePicker,Loading,showToast,Picker} from 'vant';
import 'vant/lib/index.css';
import { ref } from 'vue';
import {storage,db} from "../main.js";
import {ref as refs, uploadBytesResumable,getDownloadURL } from "firebase/storage";
import { doc, setDoc } from "firebase/firestore"; 
import {md5 } from 'js-md5';
import H5Cropper  from 'vue-cropper-h5'
import "vue-cropper-h5/dist/style.css";






export default {
  name: 'PetCreate',
  props: {
    info: Object,
    uid:String
  },
  
  setup() {
    
    var birthday = ref('');
    var date = new Date()
    const currentDate = ref([date.getFullYear()+"", (date.getMonth() + 1)+"", date.getDate()+""]);

    const showPickdate = ref(false);
    const onConfirm = ({ selectedValues }) => {
      birthday.value = selectedValues.join('/');
      showPickdate.value = false;
    };

    // 体重单位选择
    const weightUnitColumns = [
      { text: 'KG', value: 'KG' },
      { text: 'LB', value: 'LB' }
    ];
    const showWeightUnitPicker = ref(false);
    const onWeightUnitConfirm = ({ selectedOptions }) => {
      weightUnit.value = selectedOptions[0].value;
      showWeightUnitPicker.value = false;
    };
    const weightUnit = ref('KG');
    const weightValue = ref('');
    
    // 切换体重单位
    const toggleWeightUnit = () => {
      weightUnit.value = weightUnit.value === 'KG' ? 'LB' : 'KG';
    };

    return {
      birthday,
      onConfirm,
      showPickdate,
      currentDate,
      maxDate:date,
      minDate: new Date(2000, 0, 1),
      weightUnitColumns,
      showWeightUnitPicker,
      onWeightUnitConfirm,
      weightUnit,
      weightValue,
      toggleWeightUnit,
      option: {
        outputType:"jpeg",
        cancelButtonText:"Cancel",
        confirmButtonText:" OK ",
        confirmButtonBackgroundColor:"#5072ff",
        ceilbutton:"true"

      }, //配置

    };
  },
  components:{
    [Form.name]:Form,
    [Field.name]:Field,
    [CellGroup.name]:CellGroup,
    [Button.name]:Button,
    [Uploader.name]:Uploader,
    [DatePicker.name]:DatePicker,
    [Popup.name]:Popup,
    [Loading.name]:Loading,
    [showToast.name]:showToast,
    [Picker.name]:Picker,
    H5Cropper
  },
  created(){
    this.initeditInfo()
   },
  data(){
    return{
      petname:"",
      weight:"",
      phone:"",
      email:"",
      note:"",
      modify_key:"",
      image_ref:"",
      loading_state:false,
      fileList:[],
      showcrop:false,
      img:"",
      filee:{},
      index:0,
    }

  },
  methods: {
    formatDate(date) {
      return `${date.getMonth() + 1}/${date.getDate()}`;
    },
    initeditInfo(){
      if(this.info != undefined){
        this.petname = this.info.Petname;
        
        // 解析体重字符串，分离数值和单位
        const weightStr = this.info.Weight || '';
        const weightMatch = weightStr.match(/^([\d.]+)\s*(KG|LB)?$/i);
        if (weightMatch) {
          this.weightValue = weightMatch[1];
          this.weightUnit = weightMatch[2] ? weightMatch[2].toUpperCase() : 'KG';
        } else {
          this.weightValue = weightStr;
          this.weightUnit = 'KG';
        }
        
        this.phone = this.info.Phone;
        this.email = this.info.Email;
        this.note = this.info.Note;
        this.birthday = this.info.Birthday;
        this.image_ref = this.info.Image;
        if(this.info.Image != ""){
                getDownloadURL(refs(storage,this.info.Image)).then((downloadURL) => {
                  console.log('File available at', downloadURL);
                  this.fileList.push({url:downloadURL})
                  this.imageUrl = downloadURL
                });
            }
        }
    },
    formatter(type, val) {
      if (type === 'year') {
        return `${val}/`;
      } else if (type === 'month') {
        return `${val}/`;
      }
      return val;
    },

 
// 压缩图片的函数
    compressImage(file, maxWidth, maxHeight, quality, outputFormat) {
      return new Promise((resolve, reject) => {
        // 创建FileReader读取文件
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = e => {
          const img = new Image();
          img.src = e.target.result;
      
          img.onload = () => {
            // 创建Canvas用于压缩图片
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const ratio = Math.min(maxWidth / img.width, maxHeight / img.height);
            const width = img.width * ratio;
            const height = img.height * ratio;
            canvas.width = width;
            canvas.height = height;
            ctx.drawImage(img, 0, 0, width, height);
    
            // 将Canvas的内容转为blob
            canvas.toBlob(blob => {
              resolve(blob);
            }, outputFormat || file.type, quality);
          };
        };
        reader.onerror = error => reject(error);
      });
    },
    afterRead(filee) {
      var file = filee.file
      //this.showcrop = true;
      this.$refs.cropper.loadFile(file);
      this.filee = filee
      this.index = 0;
     
      // 显示上传进度提示
      showToast({
        message: this.$t('messages.select_crop_area'),
        duration: 1500
      });
    },
    canceltailor(){
      this.fileList.length = 0;
    },
    getFile(file){
      this.index++;
      if(this.index <= 1){
        return
      }

    console.log('Upload is running'+file);
        // 此时可以自行将文件上传至服务器
      const filepath = 'images/dog/'+new Date().getTime()+file.name+'.jpg'
      const storageRef = refs(storage, filepath);
      const metadata = {
        contentType: 'image/jpeg',
      };

      this.filee.file = file
      var filee = this.filee
      filee.status = 'uploading';
      filee.message = 'uploading...';


      this.compressImage(file, 1024, 1024, 0.85, 'image/jpeg').then(compressedFile => {
        const uploadTask =  uploadBytesResumable(storageRef, compressedFile,metadata)
        
        // 显示上传中提示
        showToast({
          message: this.$t('messages.uploading_image'),
          duration: 0,
          forbidClick: true,
          loadingType: 'spinner'
        });
        
      uploadTask.on('state_changed', 
        (snapshot) => {
          // Observe state change events such as progress, pause, and resume
          // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.log('Upload is ' + progress + '% done');
          switch (snapshot.state) {
            case 'paused':
              console.log('Upload is paused');
              break;
            case 'running':
              console.log('Upload is running');
              break;
          }
        }, 
        (error) => {
          console.log('File available at', error);
          filee.status = 'failed';
          filee.message = 'Upload Failed';
          
          showToast({
            message: this.$t('messages.upload_failed'),
            type: 'fail',
            duration: 2000
          });

          // Handle unsuccessful uploads
        }, 
        () => {
          filee.status = 'done';
          this.image_ref = filepath;
          if(filepath != ""){
                getDownloadURL(refs(storage,filepath)).then((downloadURL) => {
                  console.log('File available at', downloadURL);
                  this.fileList.length = 0;
                  this.fileList.push({url:downloadURL})
                  
                  showToast({
                    message: this.$t('messages.upload_success'),
                    type: 'success',
                    duration: 1500
                  });
                });
            }
          // Handle successful uploads on complete
          // For instance, get the download URL: https://firebasestorage.googleapis.com/...
        },)


      }).catch(error => {console.error(error)
         filee.status = 'failed';
          filee.message = 'Upload Failed';
          
          showToast({
            message: this.$t('messages.image_process_failed'),
            type: 'fail',
            duration: 2000
          });
      });

      // 'file' comes from the Blob or File API

    },
    async onSubmit(){
      try {
        this.loading_state = true
        
        // 组合体重值为字符串格式："数值 单位"
        const finalWeight = this.weightValue ? `${this.weightValue} ${this.weightUnit}` : '';
        
        await setDoc(doc(db, "petInfo",this.uid), {
        Petname: this.petname,
        Birthday: this.birthday,
        Image: this.image_ref,
        Weight: finalWeight,
        Phone: this.phone,
        Email: this.email,
        Note:this.note,
        Time: new Date().getTime(),
      });
      await setDoc(doc(db, "key",this.uid), {
        Modify_key:md5(this.modify_key),
      });
      
      // 显示成功提示
      showToast({
        message: this.$t('messages.save_success'),
        type: 'success',
        duration: 2000
      });
      
      this.$emit('getinfo');
      
      } catch (e) {
        console.error("Error adding document: ", e);
        showToast({
          message: this.$t('messages.save_failed'),
          type: 'fail',
          duration: 2000
        });
      } finally {
        this.loading_state = false
      }
    }
  },
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.pet-create-container {
  width: 100%;
  max-width: 600px;
  padding: 20px 0;
}

/* 头部区域 */
.header-section {
  margin-bottom: 24px;
  text-align: center;
}

.main-title {
  color: #ffffff;
  font-size: 32px;
  font-weight: 700;
  margin: 0 0 12px 0;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.subtitle {
  color: rgba(255, 255, 255, 0.9);
  font-size: 16px;
  margin: 0;
}

/* 表单卡片 */
.form-card {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  overflow: hidden;
}

.card-header {
  padding: 20px 20px 0 20px;
}

.section-title {
  color: #323233;
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 8px 0;
}

.section-tips {
  color: #969799;
  font-size: 13px;
  margin: 0;
}

/* 表单样式 */
.custom-form {
  padding: 16px;
}

.upload-section {
  display: flex;
  justify-content: center;
  padding: 20px 0;
}

.form-group {
  margin-bottom: 16px;
}

/* 密钥区域 */
.key-section {
  padding: 0 4px;
  margin-top: 24px;
}

.key-title {
  color: #323233;
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 8px 0;
}

.key-tips {
  color: #ff976a;
  font-size: 13px;
  line-height: 1.5;
  margin: 0 0 12px 0;
  padding: 0 4px;
}

/* 提交按钮 */
.submit-section {
  padding: 24px 16px 8px 16px;
}

.submit-btn {
  height: 48px;
  font-size: 16px;
  font-weight: 600;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  transition: all 0.3s ease;
}

.submit-btn:active {
  transform: scale(0.98);
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

/* Vant 组件深度定制 */
:deep(.van-cell) {
  padding: 12px 16px;
}

:deep(.van-field__label) {
  color: #323233;
  font-weight: 500;
}

:deep(.van-field__control) {
  color: #323233;
}

:deep(.van-field__control::placeholder) {
  color: #c8c9cc;
}

:deep(.van-uploader__preview-image) {
  border-radius: 8px;
  overflow: hidden;
}

/* 响应式优化 */
@media (max-width: 480px) {
  .main-title {
    font-size: 28px;
  }
  
  .subtitle {
    font-size: 14px;
  }
  
  .section-title {
    font-size: 18px;
  }
}

@media (min-width: 768px) {
  .pet-create-container {
    padding: 32px 0;
  }
  
  .custom-form {
    padding: 24px;
  }
}
</style>
